import React, { useState, useRef, useEffect } from "react";
import { X, Send, Rabbit, Tag, Instagram } from "lucide-react";
import { sendMessageToGemini } from "../services/geminiService";
import { ChatMessage, MobileActionState } from "../types";
import { getRandomVoucher, RandomVoucher } from "../services/api/bookings";
import Swal from "sweetalert2";

const QUICK_ACTIONS = [
  {
    title: "Fast Track",
    icon: "🎟️",
    benefit: "Akses Prioritas",
    desc: "Tiket Fast Track Rp45.000 mencakup akses GIZ Park & RIW tanpa antre panjang!",
  },
  {
    title: "Digital Waterfall",
    icon: "🌊",
    benefit: "Visual 8 Meter",
    desc: "Air terjun digital interaktif yang merespon gerakan Anda. Sangat estetik untuk foto!",
  },
  {
    title: "Rabbit Castle",
    icon: "🏰",
    benefit: "Eropa Style",
    desc: "Istana kelinci bergaya Eropa dengan koloni kelinci unik dari berbagai negara.",
  },
  {
    title: "Light Ball Pool",
    icon: "🔮",
    benefit: "Sensor Gerak",
    desc: "Kolam bola raksasa dengan ribuan bola yang berubah warna secara dinamis.",
  },
];

export const AIChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "model",
      text: "Halo! Saya Zoola 🐰✨. Ada yang bisa saya bantu? Biar hemat, yuk beli tiket **Fast Track Rp45.000** saja sudah termasuk akses ke GIZ Park & RIW! 🎟️💨",
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isWiggling, setIsWiggling] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [currentVoucher, setCurrentVoucher] = useState<RandomVoucher | null>(
    null
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyNav(window.scrollY > 100);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isOpen) {
        setIsWiggling(true);
        setTimeout(() => setIsWiggling(false), 1000);
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [isOpen]);

  useEffect(() => {
    const fetchVoucher = async () => {
      const voucher = await getRandomVoucher();
      if (voucher) {
        setCurrentVoucher(voucher);
      }
    };

    fetchVoucher();

    const interval = setInterval(fetchVoucher, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleActionClick = (action: (typeof QUICK_ACTIONS)[0]) => {
    Swal.fire({
      title: `<span class="font-black uppercase tracking-tight text-xl">${action.title}</span>`,
      html: `
                <div class="p-4">
                    <div class="text-5xl mb-6 animate-bounce">${action.icon}</div>
                    <p class="text-[10px] font-black uppercase tracking-widest text-indigo-500 mb-2">${action.benefit}</p>
                    <p class="text-sm text-slate-600 font-medium leading-relaxed">${action.desc}</p>
                </div>
            `,
      showConfirmButton: true,
      confirmButtonText: "OKE, MENARIK!",
      confirmButtonColor: "#4F46E5",
      padding: "2rem",
      background: "#ffffff",
      customClass: {
        popup: "rounded-[2.5rem] border-none shadow-2xl",
        confirmButton:
          "rounded-2xl px-8 py-3 font-black text-[10px] uppercase tracking-widest",
      },
    });
  };

  const typeMessage = (fullText: string) => {
    const newMessageId = (Date.now() + 1).toString();
    const speed = 25;
    let currentText = "";
    let charIndex = 0;

    setMessages((prev) => [
      ...prev,
      {
        id: newMessageId,
        role: "model",
        text: "",
        timestamp: new Date(),
      },
    ]);

    const typingInterval = setInterval(() => {
      if (charIndex < fullText.length) {
        currentText += fullText.charAt(charIndex);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === newMessageId ? { ...msg, text: currentText } : msg
          )
        );
        charIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, speed);
  };

  const handleSend = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      text: inputText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputText("");
    setIsLoading(true);

    try {
      const responseText = await sendMessageToGemini(userMsg.text);
      setIsLoading(false);
      typeMessage(responseText);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      typeMessage(
        "Waduh, sepertinya koneksi saya sedang terganggu. Coba tanya lagi nanti ya! 🐰"
      );
    }
  };

  return (
    <>
      {/* Floating Trigger Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={`fixed right-3 md:right-8 md:bottom-8 bg-slate-900 text-white p-3 md:p-5 rounded-full md:rounded-[2rem] shadow-xl md:shadow-2xl hover:scale-110 transition-all duration-300 z-[70] flex items-center justify-center border border-white/10 group active:scale-95 ${
            showStickyNav ? "bottom-24" : "bottom-5"
          } ${isWiggling ? "animate-bounce" : ""}`}
        >
          <div className="relative">
            <Rabbit className="w-5 h-5 md:w-7 md:h-7" />
            <span className="absolute -top-1 -right-1 flex h-2 w-2 md:h-2.5 md:w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 md:h-2.5 md:w-2.5 bg-emerald-500"></span>
            </span>
          </div>
          <span className="font-black text-[10px] md:text-[11px] uppercase tracking-[0.2em] pr-2 hidden md:inline">
            Tanya Zoola AI
          </span>
          <span className="font-black text-[9px] uppercase tracking-widest md:hidden">
            Chat
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed inset-0 md:inset-auto md:bottom-8 md:right-8 w-full md:w-[420px] h-[100dvh] md:h-[650px] bg-white md:rounded-[3rem] shadow-[0_30px_100px_rgba(0,0,0,0.25)] flex flex-col z-[100] border-l md:border border-slate-100 animate-slideUp overflow-hidden">
          {/* Header */}
          <div className="bg-slate-950 p-6 md:p-7 flex justify-between items-center text-white shrink-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg transform rotate-3">
                <Rabbit className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="font-black text-[13px] uppercase tracking-[0.2em] leading-none">
                  Chat Zoola AI
                </h3>
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                  <p className="text-[9px] text-emerald-400 font-black uppercase tracking-widest">
                    Digital Guide Aktif
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                  <a
                    href="https://www.instagram.com/giz.park/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[9px] text-white/70 hover:text-white transition-colors"
                  >
                    <Instagram className="w-3 h-3" />
                    <span className="font-bold">@giz.park</span>
                  </a>
                  <a
                    href="https://www.tiktok.com/@giz.park"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[9px] text-white/70 hover:text-white transition-colors"
                  >
                    <svg
                      className="w-3 h-3"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                    <span className="font-bold">@giz.park</span>
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  <a
                    href="https://www.instagram.com/rabbitswonderlandsby/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[9px] text-white/70 hover:text-white transition-colors"
                  >
                    <Instagram className="w-3 h-3" />
                    <span className="font-bold">@rabbitswonderlandsby</span>
                  </a>
                  <a
                    href="https://www.tiktok.com/@rabbitswonderlandsby"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[9px] text-white/70 hover:text-white transition-colors"
                  >
                    <svg
                      className="w-3 h-3"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                    <span className="font-bold">@rabbitswonderlandsby</span>
                  </a>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/10 p-3 rounded-2xl transition-colors bg-white/5 border border-white/5"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Voucher Banner */}
          {currentVoucher && (
            <div className="bg-indigo-600 text-white px-6 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="bg-white/10 p-2 rounded-xl">
                  <Tag className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-widest opacity-80 leading-none mb-1">
                    {currentVoucher.partnerName
                      ? `Voucher ${currentVoucher.partnerName}`
                      : "Promo Khusus"}
                  </p>
                  <p className="font-black text-xs uppercase tracking-[0.05em]">
                    KODE: {currentVoucher.code} 🏷️
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(currentVoucher.code);
                  setCopyStatus(currentVoucher.code);
                  setTimeout(() => setCopyStatus(null), 2000);
                }}
                className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all ${
                  copyStatus === currentVoucher.code
                    ? "bg-emerald-500 border-emerald-400"
                    : "bg-white/10 border-white/20"
                }`}
              >
                {copyStatus === currentVoucher.code ? "Tersalin" : "Salin"}
              </button>
            </div>
          )}

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50 scrollbar-hide">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${
                  msg.role === "user" ? "justify-end" : "justify-start"
                } animate-fadeIn`}
              >
                <div
                  className={`max-w-[85%] p-4 rounded-[1.8rem] text-sm font-medium leading-relaxed ${
                    msg.role === "user"
                      ? "bg-indigo-600 text-white rounded-br-none shadow-xl shadow-indigo-100"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-none shadow-sm"
                  }`}
                >
                  {msg.text.split("**").map((part, i) =>
                    i % 2 === 1 ? (
                      <strong
                        key={i}
                        className="font-black text-indigo-600 underline decoration-indigo-100 underline-offset-4"
                      >
                        {part}
                      </strong>
                    ) : (
                      part
                    )
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 p-4 rounded-2xl rounded-bl-none shadow-sm">
                  <div className="flex gap-1.5">
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-100"></div>
                    <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-200"></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Rewards/Benya Section */}
          <div className="bg-white border-t border-slate-100 shrink-0">
            <div
              className="px-4 py-3 overflow-x-auto scrollbar-hide"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="flex gap-2 w-max">
                {QUICK_ACTIONS.map((action, i) => (
                  <button
                    key={`quick-action-${action.title}-${i}`}
                    onClick={() => handleActionClick(action)}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl hover:border-indigo-600 hover:bg-indigo-50 transition-all group"
                  >
                    <span className="text-base group-hover:scale-110 transition-transform">
                      {action.icon}
                    </span>
                    <span className="text-[9px] font-bold text-slate-900 uppercase tracking-wider whitespace-nowrap">
                      {action.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Input Area */}
          <div className="p-6 bg-white border-t border-slate-100 pb-12 md:pb-8">
            <div className="flex gap-3 items-center bg-slate-100 border-2 border-transparent focus-within:border-indigo-600 focus-within:bg-white rounded-[2rem] pl-6 pr-2 py-2 transition-all shadow-inner">
              <input
                type="text"
                placeholder="Tanya apa saja..."
                className="flex-1 bg-transparent py-2 text-sm font-bold text-slate-900 focus:outline-none placeholder:text-slate-400"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
              />
              <button
                onClick={handleSend}
                disabled={isLoading || !inputText.trim()}
                className="bg-slate-900 text-white p-3.5 rounded-2xl hover:bg-giz-primary disabled:opacity-40 transition-all shadow-xl active:scale-90"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
